import type { EnergySport, TriathlonSplitTimes } from "./energyCalculator";

export const DISTANCE_PRESET_CUSTOM = "custom" as const;

export type TriathlonDistancePreset = {
  id: string;
  label: string;
  swimM: number;
  bikeKm: number;
  runKm: number;
  /** En-tidsläge: total tävlingstid */
  defaultTotalHours?: number;
  defaultTotalMinutes?: number;
  /** Dela tid per gren — typiska måltider (riktlinje) */
  defaultSplit?: TriathlonSplitTimes;
};

/** Standard triathlon: OD, halv iron, ironman */
export const TRIATHLON_DISTANCE_PRESETS: TriathlonDistancePreset[] = [
  {
    id: "olympic",
    label: "Olympisk distans",
    swimM: 1500,
    bikeKm: 40,
    runKm: 10,
    defaultTotalHours: 2,
    defaultTotalMinutes: 30,
    defaultSplit: {
      swimH: 0,
      swimM: 30,
      bikeH: 1,
      bikeM: 15,
      runH: 0,
      runM: 45,
    },
  },
  {
    id: "middle",
    label: "Medeldistans",
    swimM: 1900,
    bikeKm: 90,
    runKm: 21.1,
    defaultTotalHours: 6,
    defaultTotalMinutes: 0,
    defaultSplit: {
      swimH: 0,
      swimM: 45,
      bikeH: 3,
      bikeM: 15,
      runH: 2,
      runM: 0,
    },
  },
  {
    id: "full",
    label: "Full distans",
    swimM: 3800,
    bikeKm: 180,
    runKm: 42.195,
    defaultTotalHours: 11,
    defaultTotalMinutes: 30,
    defaultSplit: {
      swimH: 1,
      swimM: 15,
      bikeH: 5,
      bikeM: 45,
      runH: 4,
      runM: 0,
    },
  },
];

export type SimpleDistancePreset = {
  id: string;
  label: string;
  totalKm: number;
  defaultHours?: number;
  defaultMinutes?: number;
  defaultSeconds?: number;
};

export const CYCLING_DISTANCE_PRESETS: SimpleDistancePreset[] = [
  {
    id: "vattern315",
    label: "Vätternrundan 315 km",
    totalKm: 315,
    defaultHours: 10,
    defaultMinutes: 0,
    defaultSeconds: 0,
  },
  {
    id: "halvvattern150",
    label: "Halvvättern 150 km",
    totalKm: 150,
    defaultHours: 5,
    defaultMinutes: 30,
    defaultSeconds: 0,
  },
  {
    id: "vattern100",
    label: "Vätternrundan 100 km",
    totalKm: 100,
    defaultHours: 3,
    defaultMinutes: 30,
    defaultSeconds: 0,
  },
];

export const RUNNING_DISTANCE_PRESETS: SimpleDistancePreset[] = [
  {
    id: "marathon",
    label: "Marathon",
    totalKm: 42.195,
    defaultHours: 4,
    defaultMinutes: 0,
    defaultSeconds: 0,
  },
  {
    id: "half_marathon",
    label: "Halvmarathon",
    totalKm: 21.0975,
    defaultHours: 2,
    defaultMinutes: 0,
    defaultSeconds: 0,
  },
  {
    id: "lidingo30",
    label: "Lidingöloppet 30 km",
    totalKm: 30,
    defaultHours: 2,
    defaultMinutes: 45,
    defaultSeconds: 0,
  },
  {
    id: "km10",
    label: "10 km",
    totalKm: 10,
    defaultHours: 1,
    defaultMinutes: 0,
    defaultSeconds: 0,
  },
  {
    id: "km5",
    label: "5 km",
    totalKm: 5,
    defaultHours: 0,
    defaultMinutes: 30,
    defaultSeconds: 0,
  },
];

export const SKIING_DISTANCE_PRESETS: SimpleDistancePreset[] = [
  {
    id: "vasa90",
    label: "Vasaloppet 90 km",
    totalKm: 90,
    defaultHours: 8,
    defaultMinutes: 30,
    defaultSeconds: 0,
  },
  {
    id: "vasa45",
    label: "Vasaloppet 45 km",
    totalKm: 45,
    defaultHours: 4,
    defaultMinutes: 15,
    defaultSeconds: 0,
  },
  {
    id: "vasa30",
    label: "Vasaloppet 30 km",
    totalKm: 30,
    defaultHours: 3,
    defaultMinutes: 0,
    defaultSeconds: 0,
  },
];

export function triathlonTotalKm(p: TriathlonDistancePreset): number {
  return p.swimM / 1000 + p.bikeKm + p.runKm;
}

export function getTriathlonPresetById(
  presetId: string
): TriathlonDistancePreset | undefined {
  return TRIATHLON_DISTANCE_PRESETS.find((p) => p.id === presetId);
}

/** km för cykel/löp/skidor-förval; null om id saknas eller triathlon */
export function getSimpleDistancePresetKm(
  sport: EnergySport,
  presetId: string
): number | null {
  if (sport === "triathlon") return null;
  const list =
    sport === "cycling"
      ? CYCLING_DISTANCE_PRESETS
      : sport === "running"
        ? RUNNING_DISTANCE_PRESETS
        : SKIING_DISTANCE_PRESETS;
  const row = list.find((p) => p.id === presetId);
  return row != null ? row.totalKm : null;
}

/** Visa förinställt tal i fält (behåll decimaler som i datan) */
export function formatPresetNumberForField(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return String(n);
}

export function defaultDistancePresetId(sport: EnergySport): string {
  switch (sport) {
    case "triathlon":
      return "olympic";
    case "cycling":
      return "vattern315";
    case "running":
      return "marathon";
    case "skiing":
      return "vasa90";
  }
}

export type DefaultClockParts = {
  hours: number;
  minutes: number;
  seconds: number;
};

/** Förvald klocktid för löpning / cykel / skidor när en namngiven distans väljs (inte "Ange egen"). */
export function getDefaultDurationForSimplePreset(
  sport: EnergySport,
  presetId: string
): DefaultClockParts | null {
  if (presetId === DISTANCE_PRESET_CUSTOM) return null;
  const list =
    sport === "cycling"
      ? CYCLING_DISTANCE_PRESETS
      : sport === "running"
        ? RUNNING_DISTANCE_PRESETS
        : sport === "skiing"
          ? SKIING_DISTANCE_PRESETS
          : null;
  if (!list) return null;
  const row = list.find((p) => p.id === presetId);
  if (!row) return null;
  if (
    row.defaultHours == null &&
    row.defaultMinutes == null &&
    row.defaultSeconds == null
  ) {
    return null;
  }
  return {
    hours: row.defaultHours ?? 0,
    minutes: row.defaultMinutes ?? 0,
    seconds: row.defaultSeconds ?? 0,
  };
}

/** Total tid + gren-för-gren för triathlonförval. */
export function getTriathlonPresetTimeDefaults(
  presetId: string
): {
  total: { hours: number; minutes: number };
  split: TriathlonSplitTimes;
} | null {
  if (presetId === DISTANCE_PRESET_CUSTOM) return null;
  const p = TRIATHLON_DISTANCE_PRESETS.find((x) => x.id === presetId);
  if (!p || p.defaultTotalHours == null || !p.defaultSplit) return null;
  return {
    total: {
      hours: p.defaultTotalHours,
      minutes: p.defaultTotalMinutes ?? 0,
    },
    split: p.defaultSplit,
  };
}

function parsePositiveFloat(s: string): number | null {
  const t = s.trim().replace(",", ".");
  if (t === "") return null;
  const n = parseFloat(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Total tävlingsdistans i km (triathlon: sim m + cykel km + löp km).
 * null om eget val saknar giltiga värden.
 */
export function resolveRaceDistanceKm(
  sport: EnergySport,
  presetId: string,
  custom: {
    customKm: string;
    customTriSwimM: string;
    customTriBikeKm: string;
    customTriRunKm: string;
  }
): number | null {
  if (presetId === DISTANCE_PRESET_CUSTOM) {
    if (sport === "triathlon") {
      const sm = parsePositiveFloat(custom.customTriSwimM);
      const bk = parsePositiveFloat(custom.customTriBikeKm);
      const rk = parsePositiveFloat(custom.customTriRunKm);
      if (sm == null || bk == null || rk == null) return null;
      return sm / 1000 + bk + rk;
    }
    return parsePositiveFloat(custom.customKm);
  }

  if (sport === "triathlon") {
    const p = TRIATHLON_DISTANCE_PRESETS.find((x) => x.id === presetId);
    return p != null ? triathlonTotalKm(p) : null;
  }

  const list =
    sport === "cycling"
      ? CYCLING_DISTANCE_PRESETS
      : sport === "running"
        ? RUNNING_DISTANCE_PRESETS
        : SKIING_DISTANCE_PRESETS;
  const row = list.find((x) => x.id === presetId);
  return row != null ? row.totalKm : null;
}
